import type { ReactNode } from 'react';
import React, { Children } from 'react';
import cn from 'classnames';

import type { IDeps, TClassNames, TColumnsMargin, TColumnsWidth } from '../models';

import { mapWidth, mapShrink, getColumnStyle, mapMargin } from './column-styles';

interface IRenderColsProps {
    colAutoWidth: boolean;
    collapse: boolean;
    columnsMargin: TColumnsMargin;
    columnsWidth: TColumnsWidth | TColumnsWidth[];
    vertical: boolean;
}

export const renderCols = (
    children: ReactNode,
    props: IRenderColsProps,
    classNames: TClassNames,
    styles: IDeps['styles']
) => {
    const { colAutoWidth, collapse, columnsMargin, columnsWidth, vertical, ...rest } = props;

    const marginDirection = vertical ? 'marginBottom' : 'marginRight';

    const cols = Children.toArray(children);
    const getWidth = getColumnStyle('width', mapWidth);
    const getShrink = getColumnStyle('flexShrink', mapShrink);
    const getMargin = getColumnStyle(marginDirection, mapMargin);

    // 25 - это default value для отступа между columns.
    // Когда перейдем на 24px по всему сайту, то уберем это.
    const isDefaultMargin = columnsMargin === 25;

    const columnStyles = cols.map((_, i) => ({
        ...getWidth(i, columnsWidth),
        ...getShrink(i, columnsWidth),
        ...(isDefaultMargin ? {} : getMargin(i, columnsMargin)),
    }));

    return cols.map((col, i) => (
        <div
            // eslint-disable-next-line react/no-array-index-key
            key={i}
            className={cn(
                styles.col,
                {
                    [styles.colAutoWidth]: colAutoWidth,
                    [styles.collapse]: collapse,
                },
                classNames.col
            )}
            style={columnStyles[i]}
            {...rest}
        >
            {col}
        </div>
    ));
};
